/**
 * SkPicture recorders — the static-plane core.
 *
 * `recordFloorPicture` collapses N objects into ONE GPU-replayable picture:
 * it runs ONLY on committed change (revision bump / LOD bucket change /
 * cull-window change), never per frame — pan/zoom re-blit the recorded
 * picture under the camera transform with zero JS work.
 *
 * @module floorplan/renderer/recordPicture
 * @category FloorPlan
 */

import type { SkPicture } from '@shopify/react-native-skia';
import { PaintStyle, Skia } from '@shopify/react-native-skia';

import type { Bounds } from '../models/geometry';
import type { FloorObject } from '../models/object';
import { resolveRenderer } from './RendererRegistry';
import type { RenderContext } from './types';

/** Record `objects` (already culled + in render order) into one picture. */
export const recordFloorPicture = (
  objects: readonly FloorObject[],
  ctx: RenderContext,
  bounds: Bounds,
): SkPicture => {
  const recorder = Skia.PictureRecorder();
  const canvas = recorder.beginRecording(
    Skia.XYWHRect(bounds.x, bounds.y, bounds.width, bounds.height),
  );
  for (const object of objects) {
    resolveRenderer(object.type).draw(canvas, object, ctx);
  }
  return recorder.finishRecordingAsPicture();
};

/**
 * Record the grid + plan surface. Depends only on plan size, grid size, and
 * theme — re-records on those alone (not on object edits). One SkPath for
 * all lines keeps it a single draw call.
 */
export const recordGridPicture = (
  planWidth: number,
  planHeight: number,
  gridSize: number,
  colors: { surface: string; grid: string },
): SkPicture => {
  const recorder = Skia.PictureRecorder();
  const canvas = recorder.beginRecording(
    Skia.XYWHRect(0, 0, planWidth, planHeight),
  );

  const surfacePaint = Skia.Paint();
  surfacePaint.setColor(Skia.Color(colors.surface));
  surfacePaint.setStyle(PaintStyle.Fill);
  canvas.drawRect(Skia.XYWHRect(0, 0, planWidth, planHeight), surfacePaint);

  if (gridSize > 0) {
    // PathBuilder, not the deprecated SkPath mutation API (removal announced).
    const builder = Skia.PathBuilder.Make();
    for (let x = 0; x <= planWidth; x += gridSize) {
      builder.moveTo(x, 0);
      builder.lineTo(x, planHeight);
    }
    for (let y = 0; y <= planHeight; y += gridSize) {
      builder.moveTo(0, y);
      builder.lineTo(planWidth, y);
    }
    const path = builder.detach();
    const gridPaint = Skia.Paint();
    gridPaint.setColor(Skia.Color(colors.grid));
    gridPaint.setStyle(PaintStyle.Stroke);
    gridPaint.setStrokeWidth(1);
    gridPaint.setAntiAlias(false); // axis-aligned hairlines — AA just blurs
    canvas.drawPath(path, gridPaint);
  }

  // Plan border on top of the grid.
  const borderPaint = Skia.Paint();
  borderPaint.setColor(Skia.Color(colors.grid));
  borderPaint.setStyle(PaintStyle.Stroke);
  borderPaint.setStrokeWidth(2);
  canvas.drawRect(Skia.XYWHRect(0, 0, planWidth, planHeight), borderPaint);

  return recorder.finishRecordingAsPicture();
};
